import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { authApi } from '../services/authApi';
import { authStorage } from '../services/authStorage';
import { mapStatusToAPI } from '../utils/statusUtils';
import { downloadFile } from '../utils/urlUtils';
import { isOptionalTest } from '../utils/optionalTestUtils';
import '../styles/TicketDetailModal.css';

interface Comment {
  id: number;
  author: {
    name: string;
    avatar: string;
  };
  date: string;
  text: string;
  status?: string;
}

interface TicketDetailProps {
  ticket: {
    id: number;
    ticketNumber: string;
    title?: string;
    status: 'todo' | 'inProgress' | 'passed' | 'failed';
    date: string;
    vatRate: string;
    assignee?: {
      name: string;
      avatar: string;
    };
    description?: string;
    receipt?: any;
    testNumber?: number;
    testSuiteRuleMappingId?: number; // Add this to track the mapping ID
    testName?: string; // Add testName property
  };
  comments: Comment[];
  onClose: () => void;
  onTestUpdate?: () => void; // Callback to refresh data after update
  companyId?: number; // Company ID for admin users to specify which company the test belongs to
  onMarkAsOptional?: (ticket: any) => void; // New prop for handling optional test marking
  testSuiteId?: number; // Test suite ID for ping admin functionality
}

const TicketDetailModal: React.FC<TicketDetailProps> = ({
  ticket,
  comments,
  onClose,
  onTestUpdate,
  companyId,
  onMarkAsOptional,
  testSuiteId
}) => {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string>('');
  const [uploadSuccess, setUploadSuccess] = useState<string>('');
  const [uploadedTestSuiteId, setUploadedTestSuiteId] = useState<number | null>(null);
  const [pinging, setPinging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [commentText, setCommentText] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user is admin on component mount
  useEffect(() => {
    const userData = authStorage.getUserData();
    if (userData) {
      setIsAdmin(userData.companyId === null);
    }
    // Set test suite ID if provided
    if (testSuiteId) {
      setUploadedTestSuiteId(testSuiteId);
    }
  }, [testSuiteId]);

  // Handle modal close - refresh data if company user uploaded
  const handleClose = () => {
    if (uploadSuccess && !isAdmin && onTestUpdate) {
      onTestUpdate();
    }
    onClose();
  };

  const handleFileUpload = async (file: File, status: string = 'inProgress', comment: string = '') => {
    try {
      setIsUploading(true);
      setUploadError('');

      const token = authStorage.getAccessToken();
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Check if receipt exists to determine which API to use
      if (!ticket.receipt) {
        // Use uploadReceipt API for initial upload when receipt is null
        const testRuleInfoId = ticket.testSuiteRuleMappingId || ticket.testNumber || ticket.id;

        const result = await authApi.uploadReceipt(token, {
          testRuleInfoId: testRuleInfoId,
          receiptFile: file,
          comment: comment || 'Initial receipt upload',
          companyId: companyId,
        });

        // Show success with ping button for company users
        if (!isAdmin && result.testSuiteInfoId) {
          setUploadedTestSuiteId(result.testSuiteInfoId);
          setUploadSuccess('✅ Test uploaded successfully!');
        }
      } else {
        // Use updateTest API for existing receipts
        const mappingId = ticket.testSuiteRuleMappingId || ticket.testNumber || ticket.id;

        await authApi.updateTest(token, {
          testSuiteRuleMappingId: mappingId,
          status: mapStatusToAPI(status),
          comment: comment,
          receiptFile: file,
          companyId: companyId,
        });

        // Show success with ping button for company users
        if (!isAdmin) {
          setUploadSuccess('✅ Test updated successfully!');
          // Test suite ID should already be set from prop
        }
      }

      // Close modal for admin, keep open for company users to see ping button
      if (isAdmin) {
        // Call callback to refresh data
        if (onTestUpdate) {
          onTestUpdate();
        }
        handleClose();
      }
      // Don't call onTestUpdate for company users yet - let them ping admin first
      // onTestUpdate will be called after they close the modal or ping admin
    } catch (error) {
      console.error('Failed to upload receipt:', error);
      setUploadError(error instanceof Error ? error.message : 'Failed to upload receipt');
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadCorrectReceipt = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file, 'inProgress', 'Uploaded corrected receipt');
    }
  };

  const handleStatusChange = async (newStatus: string, comment: string = '') => {
    try {
      setIsUploading(true);
      setUploadError('');

      const token = authStorage.getAccessToken();
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Permission check: 
      // - Admin users can change status and add comments
      // - Company users can ONLY add comments (when status doesn't change) but cannot change status
      if (!isAdmin && newStatus !== ticket.status) {
        throw new Error('You do not have permission to change status. You can only upload receipts and add comments.');
      }

      const mappingId = ticket.testSuiteRuleMappingId || ticket.testNumber || ticket.id;

      await authApi.updateTest(token, {
        testSuiteRuleMappingId: mappingId,
        status: mapStatusToAPI(newStatus),
        comment: comment,
        companyId: companyId, // Pass the company ID for admin users
      });

      // Call callback to refresh data
      if (onTestUpdate) {
        onTestUpdate();
      }

      // Close modal on success
      onClose();
    } catch (error) {
      console.error('Failed to update test status:', error);
      setUploadError(error instanceof Error ? error.message : 'Failed to update test status');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSendComment = () => {
    if (commentText.trim()) {
      // Send comment by calling handleStatusChange with the same status
      // This allows both admin and company users to add comments
      handleStatusChange(ticket.status, commentText.trim());
      setCommentText('');
    }
  };

  const handlePingAdmin = async () => {
    if (!uploadedTestSuiteId) return;

    try {
      setPinging(true);
      const token = authStorage.getAccessToken();
      if (!token) throw new Error('Authentication token not found');

      await authApi.pingAdmin(token, uploadedTestSuiteId);
      setUploadSuccess('✅ Admin notified successfully!');

      // Refresh data and close modal after successful ping
      if (onTestUpdate) {
        onTestUpdate();
      }

      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to ping admin:', error);
      setUploadError(error instanceof Error ? error.message : 'Failed to notify admin');
    } finally {
      setPinging(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="ticket-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-ticket-number">
            <span className="number-badge">{ticket.testNumber || ticket.id}</span>
            <span className="ticket-id">{ticket.ticketNumber}</span>
          </div>
          <button className="close-button" onClick={handleClose}>×</button>
        </div>

        <div className="modal-title">
          <h2>{ticket.testName}</h2>
        </div>

        <div className="modal-content">
          <div className="ticket-activity-feed">
            {comments.map(comment => (
              <div key={comment.id} className="activity-item">
                <div className="activity-meta">
                  <div className="activity-date">{comment.date}</div>
                  {comment.status && <div className={`status-tag status-${comment.status.toLowerCase().replace('_', '-')}`}>{comment.status.replace('_', ' ')}</div>}
                </div>
                <div className="activity-content">
                  {comment.status ? (
                    <div className="activity-status-change">
                      <strong>{t('testSuite.statusChanged', { status: comment.status.replace('_', ' ') }) || `Status changed to ${comment.status.replace('_', ' ')}`}</strong>
                      {comment.text && comment.text !== 'Updated status' && (
                        <div className="status-comment">
                          <p>{comment.text}</p>
                        </div>
                      )}
                      {comment.status === 'Failed' && !isAdmin && (
                        <div className="upload-button-container">
                          <button
                            className="upload-correct-receipt"
                            onClick={handleUploadCorrectReceipt}
                            disabled={isUploading}
                          >
                            {isUploading ? t('testSuite.uploading', 'Uploading...') : (t('testSuite.uploadCorrect') || 'Upload Correct Receipt')}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="activity-comment">
                      <div className="comment-header">
                        <div className="user-avatar">
                          <img src={comment.author.avatar} alt={comment.author.name} />
                        </div>
                        <div className="comment-author">
                          <strong>{comment.author.name}</strong>
                        </div>
                      </div>
                      <div className="comment-text">{comment.text}</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {comments.length === 0 && (
              <div className="no-activity">
                <p>{t('testSuite.noActivity') || 'No activity yet'}</p>
              </div>
            )}
          </div>
        </div>

        {uploadError && (
          <div className="error-message">
            {uploadError}
          </div>
        )}

        {uploadSuccess && (
          <div style={{
            padding: '16px',
            margin: '16px 20px',
            backgroundColor: '#d4edda',
            color: '#155724',
            border: '1px solid #c3e6cb',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            <span>{uploadSuccess}</span>
            {uploadedTestSuiteId && (uploadSuccess.includes('uploaded') || uploadSuccess.includes('updated')) && !uploadSuccess.includes('notified') && !pinging && (
              <button
                onClick={handlePingAdmin}
                disabled={pinging}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: pinging ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
                onMouseOver={(e) => !pinging && (e.currentTarget.style.backgroundColor = '#0056b3')}
                onMouseOut={(e) => !pinging && (e.currentTarget.style.backgroundColor = '#007bff')}
              >
                {pinging ? 'Notifying...' : '🔔 Ping Admin'}
              </button>
            )}
          </div>
        )}

        <div className="modal-footer">
          {/* Comment input section */}
          <div className="comment-section">
            <div className="comment-input-container">
              <input
                type="text"
                className="comment-input"
                placeholder={t('testSuite.addComment') || 'Add a comment...'}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendComment()}
                disabled={isUploading}
              />
              <div className="comment-actions">
                <button
                  className="send-button"
                  onClick={handleSendComment}
                  disabled={isUploading || !commentText.trim()}
                >
                  <span className="send-icon"></span>
                </button>
              </div>
            </div>
          </div>

          {/* Action buttons section */}
          <div className="footer-actions">
            {/* Mark as optional link - positioned at the left/start of footer actions */}
            {isOptionalTest(ticket.testName || '') && onMarkAsOptional && (
              <div className="modal-optional-link-container">
                <button
                  className="modal-optional-link"
                  onClick={() => onMarkAsOptional(ticket)}
                  disabled={isUploading}
                >
                  {t('testSuite.markAsOptional', 'Mark as optional')}
                </button>
              </div>
            )}

            {/* Show upload button when no receipt exists (for both admin and company users) */}
            {/* Extended to support admin upload functionality as requested */}
            {!ticket.receipt && (
              <button
                className="upload-receipt-button"
                onClick={handleUploadCorrectReceipt}
                disabled={isUploading}
              >
                {isUploading ? t('testSuite.uploadingReceipt') : t('testSuite.uploadReceipt')}
              </button>
            )}

            {/* Show upload button when receipt exists (for both admin and company users) */}
            {/* Extended to support admin upload functionality as requested */}
            {ticket.receipt && (
              <button
                className="upload-receipt-button"
                onClick={handleUploadCorrectReceipt}
                disabled={isUploading}
              >
                {isUploading ? t('testSuite.uploadingReceipt') : t('testSuite.uploadNewReceipt', 'Upload New Test')}
              </button>
            )}

            {/* Show download button for both admin and company users if receipt exists */}
            {ticket.receipt && ticket.receipt.receiptFileUrl && (
              <button
                className="download-receipt-button"
                onClick={() => {
                  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
                  // Extract clean filename by removing timestamp prefix
                  const rawFileName = ticket.receipt.receiptFileUrl.split('/').pop() || 'receipt';
                  const cleanFileName = rawFileName.replace(/^\d+-/, ''); // Remove timestamp prefix
                  downloadFile(baseUrl, ticket.receipt.receiptFileUrl, cleanFileName);
                }}
                title={t('testSuite.downloadReceipt') || 'Download Test'}
                disabled={isUploading}
              >
                {t('testSuite.downloadReceipt', 'Download Test')}
              </button>
            )}
          </div>
        </div>

        {/* Hidden file input for receipt upload - accepts all file types */}
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          accept="*/*"
          onChange={handleFileSelect}
        />
      </div>
    </div>
  );
};

export default TicketDetailModal;